import React, { useState, useEffect } from 'react';
import ApexCharts from 'react-apexcharts';
import { FileSpreadsheet, FileDown, Calendar, Database, TrendingUp, AlertTriangle, Bell } from 'lucide-react';

const BiogasDashboard = () => {
  const [selectedDate, setSelectedDate] = useState('2025-03-26');
  const [historyData, setHistoryData] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [logAnalyticsData, setLogAnalyticsData] = useState([]); // Moved to top
  const [isLoading, setIsLoading] = useState(false);

  // Toggle function for logs view
  const toggleLogs = () => {
    setShowLogs(!showLogs);
  };

  // Fetch data function with more comprehensive mock data
  const fetchData = () => {
    setIsLoading(true);
    setTimeout(() => {
      const mockData = [
        { time: '05:00 - 06:00', rawBioGas: 0, cbgCascades: 0 },
        { time: '06:00 - 07:00', rawBioGas: 50.5, cbgCascades: 177.60 },
        { time: '07:00 - 08:00', rawBioGas: 75.2, cbgCascades: 175.48 },
        { time: '08:00 - 09:00', rawBioGas: 120.3, cbgCascades: 220.15 },
        { time: '09:00 - 10:00', rawBioGas: 180.6, cbgCascades: 265.72 },
        { time: '10:00 - 11:00', rawBioGas: 210.4, cbgCascades: 300.55 }
      ];

      // Mock logs data (alerts and alarms)
      const mockLogs = [
        { timestamp: '2025-03-26 07:15', type: 'Alert', message: 'Low feedstock level detected in Mixer Tank', status: 'Active' },
        { timestamp: '2025-03-26 09:30', type: 'Alarm', message: 'High temperature in Anaerobic Digester (45°C)', status: 'Resolved' },
        { timestamp: '2025-03-26 10:45', type: 'Alert', message: 'Biogas pressure drop in Scrubber Unit', status: 'Active' }, // Fixed: biGas -> message
        { timestamp: '2025-03-26 11:20', type: 'Alarm', message: 'Compressor overload detected', status: 'Active' },
        { timestamp: '2025-03-26 06:50', type: 'Alert', message: 'pH imbalance in Digester (6.8)', status: 'Resolved' }
      ];

      // Mock log analytics data (e.g., count of alerts/alarms per hour)
      const mockLogAnalytics = [
        { hour: '06:00', alerts: 1, alarms: 0 },
        { hour: '07:00', alerts: 1, alarms: 0 },
        { hour: '08:00', alerts: 0, alarms: 0 },
        { hour: '09:00', alerts: 0, alarms: 1 },
        { hour: '10:00', alerts: 1, alarms: 0 },
        { hour: '11:00', alerts: 0, alarms: 1 }
      ];

      setHistoryData(mockData);
      setLogsData(mockLogs);
      setLogAnalyticsData(mockLogAnalytics);
      setIsLoading(false);
    }, 500);
  };

  // Chart configuration for Raw Bio-Gas
  const rawBioGasChartOptions = {
    series: [{
      name: 'Raw Bio-Gas',
      data: historyData.map(item => item.rawBioGas)
    }],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: historyData.map(item => item.time.split(' - ')[0]),
        title: {
          text: 'Time (Hourly)'
        }
      },
      yaxis: {
        title: {
          text: 'Raw Bio-Gas (m³/hr)'
        }
      },
      title: {
        text: 'Hourly Raw Bio-Gas Production',
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      fill: {
        colors: ['#3B82F6']
      },
      tooltip: {
        theme: 'light'
      }
    }
  };

  // Chart configuration for CBG Cascades
  const cbgCascadesChartOptions = {
    series: [{
      name: 'CBG Cascades',
      data: historyData.map(item => item.cbgCascades)
    }],
    options: {
      chart: {
        type: 'area',
        height: 350,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth'
      },
      xaxis: {
        categories: historyData.map(item => item.time.split(' - ')[0]),
        title: {
          text: 'Time (Hourly)'
        }
      },
      yaxis: {
        title: {
          text: 'CBG Cascades (kg/hr)'
        }
      },
      title: {
        text: 'Hourly CBG Filled to Cascades',
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      },
      tooltip: {
        theme: 'light'
      }
    }
  };

  // New: Log Analytics Chart (e.g., bar chart for alerts/alarms per hour)
  const logAnalyticsChartOptions = {
    series: [
      {
        name: 'Alerts',
        data: logAnalyticsData.map(item => item.alerts)
      },
      {
        name: 'Alarms',
        data: logAnalyticsData.map(item => item.alarms)
      }
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: '55%',
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: logAnalyticsData.map(item => item.hour),
        title: {
          text: 'Time (Hourly)'
        }
      },
      yaxis: {
        title: {
          text: 'Count'
        }
      },
      title: {
        text: 'Alerts & Alarms Analytics',
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      colors: ['#F59E0B', '#EF4444'], // Yellow for alerts, red for alarms
      fill: {
        opacity: 1
      },
      tooltip: {
        theme: 'light'
      }
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 p-6">
      <div className="container mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-neutral-800 flex items-center">
            <TrendingUp className="mr-3 text-blue-600" size={24} />
            Biogas Performance Dashboard
          </h1>
          <div className="flex items-center space-x-4">
            <input 
              type="date" 
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button 
              onClick={fetchData}
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 flex items-center"
            >
              {isLoading ? 'Loading...' : 'Refresh Data'}
            </button>
            {/* Toggle Switch for Logs */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-neutral-700">View Logs</label>
              <button
                onClick={toggleLogs}
                className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${showLogs ? 'bg-blue-600' : 'bg-neutral-200'}`}
              >
                <span
                  className={`inline-block w-5 h-5 transform rounded-full bg-white shadow-lg transition-transform duration-200 ease-in-out ${showLogs ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Conditional Content: Charts and Tables */}
        {!showLogs ? (
          // Production View: Charts + Data Table
          <>
            {/* Charts Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4">
                <ApexCharts 
                  options={rawBioGasChartOptions.options} 
                  series={rawBioGasChartOptions.series} 
                  type="bar" 
                  height={350} 
                />
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <ApexCharts 
                  options={cbgCascadesChartOptions.options} 
                  series={cbgCascadesChartOptions.series} 
                  type="area" 
                  height={350} 
                />
              </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-neutral-200">
                <h2 className="text-lg font-semibold text-neutral-800 flex items-center">
                  <Database className="mr-2 text-blue-600" size={20} />
                  Production Data
                </h2>
                <span className="text-sm text-neutral-500">For {selectedDate}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-100 border-b border-neutral-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">Time</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-600 uppercase tracking-wider">Raw Bio-Gas (m³/hr)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-600 uppercase tracking-wider">CBG Cascades (kg/hr)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.length > 0 ? (
                      historyData.map((row, index) => (
                        <tr 
                          key={index} 
                          className="border-b border-neutral-100 hover:bg-neutral-50 transition duration-150"
                        >
                          <td className="px-4 py-3 text-sm">{row.time}</td>
                          <td className="px-4 py-3 text-right text-sm">{row.rawBioGas.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-sm">{row.cbgCascades.toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-6 text-neutral-500">
                          No data available. Select a date and fetch data.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          // Logs View: Log Analytics Chart + Logs Table
          <>
            {/* Log Analytics Chart */}
            <div className="grid md:grid-cols-1 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4">
                <ApexCharts 
                  options={logAnalyticsChartOptions.options} 
                  series={logAnalyticsChartOptions.series} 
                  type="bar" 
                  height={350} 
                />
              </div>
            </div>

            {/* Logs Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-neutral-200">
                <h2 className="text-lg font-semibold text-neutral-800 flex items-center">
                  <Bell className="mr-2 text-yellow-500" size={20} />
                  Alerts & Alarms Logs
                </h2>
                <span className="text-sm text-neutral-500">For {selectedDate}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-100 border-b border-neutral-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">Message</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsData.length > 0 ? (
                      logsData.map((log, index) => (
                        <tr 
                          key={index} 
                          className={`border-b border-neutral-100 hover:bg-neutral-50 transition duration-150 ${
                            log.type === 'Alarm' ? 'bg-red-50' : log.type === 'Alert' ? 'bg-yellow-50' : ''
                          }`}
                        >
                          <td className="px-4 py-3 text-sm text-neutral-900">{log.timestamp}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              log.type === 'Alarm' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {log.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{log.message}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              log.status === 'Active' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {log.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-6 text-neutral-500">
                          No logs available for the selected date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Download Section */}
        <div className="mt-6 flex justify-end space-x-4">
          <button 
            className="flex items-center bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md transition duration-300"
            onClick={() => alert(showLogs ? 'Logs CSV download to be implemented' : 'CSV download to be implemented')}
          >
            <FileSpreadsheet className="mr-2" size={20} /> Download CSV
          </button>
          <button 
            className="flex items-center bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md transition duration-300"
            onClick={() => alert(showLogs ? 'Logs PDF download to be implemented' : 'PDF download to be implemented')}
          >
            <FileDown className="mr-2" size={20} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default BiogasDashboard;